/**
 * Stripe Bluetooth Reader Service (WisePad 3)
 * Manages discovery, connection, and payment processing via Bluetooth reader.
 */
import { supabase } from '../lib/supabaseClient';

import type { ReaderInterface } from '@capacitor-community/stripe-terminal';

import {
  StripeTerminal,
  TerminalConnectTypes,
  TerminalEventsEnum,
} from '@capacitor-community/stripe-terminal';

type PluginListenerHandle = { remove: () => Promise<void> };

export type ReaderPaymentParams = {
  businessId: string;
  amount: number;
  paymentType: 'deposit' | 'final' | 'partial';
  bookingId?: string | null;
  proposalId?: string | null;
  description?: string;
};

export type ReaderStatus =
  | 'idle'
  | 'discovering'
  | 'connecting'
  | 'connected'
  | 'processing'
  | 'waiting_for_card'
  | 'confirming'
  | 'success'
  | 'error';

export type ReaderDisplayEvent = {
  message: string;
  timestamp: number;
};

type StatusCallback = (status: ReaderStatus, message?: string) => void;
type DisplayCallback = (event: ReaderDisplayEvent) => void;
type ReaderChangeCallback = (reader: ReaderInterface | null) => void;
type DiscoveredReadersCallback = (readers: ReaderInterface[]) => void;

/** Only use Stripe test/simulated readers when explicitly enabled (real WisePad 3 requires live mode). */
const isTestMode = import.meta.env.VITE_STRIPE_TEST_MODE === 'true';

/** iOS plugin divides this by 1000 → seconds to wait before resolving (see StripeTerminal.swift). */
const BLUETOOTH_SCAN_MS = 20_000;
/** Hard cap for the entire discover flow (token + native scan). */
const DISCOVERY_OVERALL_TIMEOUT_MS = 25_000;
const TOKEN_FETCH_TIMEOUT_MS = 12_000;

let activeDiscoveryGeneration = 0;

/** Last received locationId — safe to cache (changes rarely). Tokens themselves are NEVER cached: Stripe Terminal tokens are single-use. */
let cachedLocationId: string | null = null;

/**
 * Remember the last reader a business successfully connected to, so we can silently
 * reconnect to it next time instead of making the cashier pick from a list at checkout.
 */
const rememberedReaderKey = (businessId: string) => `stripe_last_reader_${businessId}`;

export function getRememberedReaderSerial(businessId: string): string | null {
  try {
    return localStorage.getItem(rememberedReaderKey(businessId));
  } catch {
    return null;
  }
}

function rememberReader(businessId: string, serialNumber: string | undefined | null) {
  if (!serialNumber) return;
  try {
    localStorage.setItem(rememberedReaderKey(businessId), serialNumber);
  } catch {
    // localStorage unavailable — non-fatal, we just won't auto-reconnect next time.
  }
}

/** Latest snapshot of reader state — read by the UI via polling for resilience to callback unregistration. */
let lastStatus: ReaderStatus = 'idle';
let lastStatusMessage: string | null = null;
let lastDisplayMessage: string = '';
let firmwareUpdateProgress: number | null = null;
let firmwareUpdating = false;

let connectedReader: ReaderInterface | null = null;
let initialized = false;
let tokenListener: PluginListenerHandle | null = null;
let disconnectListener: PluginListenerHandle | null = null;
let displayMessageListener: PluginListenerHandle | null = null;
let inputRequestListener: PluginListenerHandle | null = null;
let batteryListener: PluginListenerHandle | null = null;

let currentBusinessId: string | null = null;
let statusCallback: StatusCallback | null = null;
let displayCallback: DisplayCallback | null = null;
let readerChangeCallback: ReaderChangeCallback | null = null;
let discoveredReadersCallback: DiscoveredReadersCallback | null = null;
let discoveredReadersListener: PluginListenerHandle | null = null;
let updateAvailableListener: PluginListenerHandle | null = null;
let updateStartListener: PluginListenerHandle | null = null;
let updateProgressListener: PluginListenerHandle | null = null;
let updateFinishListener: PluginListenerHandle | null = null;

function emitStatus(status: ReaderStatus, message?: string) {
  lastStatus = status;
  lastStatusMessage = message ?? null;
  if (statusCallback) statusCallback(status, message);
}

function emitDisplay(message: string) {
  lastDisplayMessage = message;
  if (displayCallback) displayCallback({ message, timestamp: Date.now() });
}

/** Snapshot getters — UI polls these so it stays in sync even if React callbacks get unregistered. */
export function getReaderSnapshot() {
  return {
    status: lastStatus,
    statusMessage: lastStatusMessage,
    displayMessage: lastDisplayMessage,
    firmwareProgress: firmwareUpdateProgress,
    firmwareUpdating,
    connectedReader,
  };
}

export function onStatusChange(cb: StatusCallback) {
  statusCallback = cb;
}

export function onDisplayMessage(cb: DisplayCallback) {
  displayCallback = cb;
}

export function onReaderChange(cb: ReaderChangeCallback) {
  readerChangeCallback = cb;
}

export function onDiscoveredReaders(cb: DiscoveredReadersCallback) {
  discoveredReadersCallback = cb;
}

export function removeCallbacks() {
  statusCallback = null;
  displayCallback = null;
  readerChangeCallback = null;
  discoveredReadersCallback = null;
}

/**
 * Fetches a fresh Stripe Terminal connection token from our backend.
 * Tokens are single-use per Stripe spec — NEVER cache them.
 */
export async function fetchConnectionToken(
  businessId: string
): Promise<{ secret: string; locationId: string }> {
  const { data: sessionData } = await supabase.auth.getSession();
  let accessToken = sessionData?.session?.access_token;
  if (!accessToken) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    accessToken = refreshed?.session?.access_token;
  }
  if (!accessToken) {
    throw new Error('You are signed out. Please sign in again to use the card reader.');
  }

  console.log('[readerService] fetching fresh connection token');
  const { data, error } = await supabase.functions.invoke('terminal-connection-token', {
    body: { businessId },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) {
    const status = (error as { context?: { status?: number } })?.context?.status;
    if (status === 401) {
      throw new Error('Session expired. Please sign out and sign back in.');
    }
    throw new Error(error.message || 'Failed to get Terminal connection');
  }
  if (!data?.secret) {
    throw new Error(data?.error || 'Failed to get Terminal connection');
  }
  if (!data.locationId) {
    throw new Error(
      'Terminal location not configured. Open Stripe Dashboard → Terminal → Locations for this Connect account.'
    );
  }
  console.log('[readerService] connection token diagnostics:', {
    locationId: data.locationId,
    tipsEnabled: data.tipsEnabled,
    tippingConfigId: data.tippingConfigId,
    tippingSyncError: data.tippingSyncError,
  });
  cachedLocationId = data.locationId as string;
  return { secret: data.secret as string, locationId: cachedLocationId };
}

function notifyReaderChange() {
  if (readerChangeCallback) readerChangeCallback(connectedReader);
}

async function syncConnectedReaderFromSdk(): Promise<void> {
  try {
    const terminal = getTerminal();
    const { reader } = await terminal.getConnectedReader();
    connectedReader = reader ?? null;
  } catch {
    // Fall back to last known reader reference
  }
  notifyReaderChange();
}

export function getConnectedReader(): ReaderInterface | null {
  return connectedReader;
}

export function isReaderConnected(): boolean {
  return connectedReader !== null;
}

/**
 * Returns the StripeTerminal plugin synchronously.
 * IMPORTANT: never `await` the plugin proxy directly — Capacitor proxies have a
 * `.then` property that triggers `"StripeTerminal.then() is not implemented on ios"`.
 */
function getTerminal() {
  return StripeTerminal;
}

/**
 * Initializes the Stripe Terminal SDK and registers the token listener.
 * IMPORTANT: returns void, not the plugin proxy. Returning the proxy from an
 * async function triggers `Promise.resolve(thenable).then(...)` which calls
 * `.then` on the proxy → "not implemented on ios" error.
 *
 * This is THE ONLY token provider for the whole app (Tap to Pay delegates
 * here too). The native SDK keeps one pending completion per token request —
 * a second listener answering the same request would hit "Stripe Terminal do
 * not pending fetchConnectionToken", so never register another one.
 */
export async function ensureInitialized(businessId: string): Promise<void> {
  const terminal = getTerminal();

  if (initialized && currentBusinessId === businessId) return;

  if (tokenListener) {
    await tokenListener.remove();
    tokenListener = null;
  }

  currentBusinessId = businessId;

  tokenListener = await terminal.addListener(
    TerminalEventsEnum.RequestedConnectionToken,
    async () => {
      const bizId = currentBusinessId;
      if (!bizId) return;
      try {
        console.log('[readerService] SDK requested connection token — fetching fresh one');
        const { secret } = await fetchConnectionToken(bizId);
        await terminal.setConnectionToken({ token: secret });
      } catch (err) {
        console.error('[readerService] connection token failed:', err);
        emitStatus('error', err instanceof Error ? err.message : 'Terminal connection failed');
        // Answer with an empty token so the pending native request fails NOW
        // instead of hanging for the SDK's 60-second provider timeout.
        try {
          await terminal.setConnectionToken({ token: '' });
        } catch {
          // Native rejects the empty-token call after failing the completion — expected.
        }
      }
    }
  );

  if (!initialized) {
    await terminal.initialize({ isTest: isTestMode ? true : false });
    initialized = true;
  }
}

/**
 * Discover nearby Bluetooth readers.
 * Returns list of found readers (WisePad 3 etc.)
 */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

async function cancelDiscovery(): Promise<void> {
  try {
    const terminal = getTerminal();
    await terminal.cancelDiscoverReaders();
  } catch {
    // No active discovery session
  }
}

/**
 * Stop an in-progress search and reset UI state (safe to call from Cancel button).
 * Does NOT await native operations — fires them and returns immediately so the UI
 * never blocks if the plugin is unresponsive.
 */
export function cancelReaderDiscovery(): void {
  console.log('[readerService] cancelReaderDiscovery — bumping generation, emitting idle');
  activeDiscoveryGeneration += 1;
  emitStatus('idle');
  cancelDiscovery().catch((err) => console.warn('[readerService] cancelDiscovery error:', err));
  removeDiscoveredReadersListener().catch((err) =>
    console.warn('[readerService] removeListener error:', err)
  );
}

async function removeDiscoveredReadersListener(): Promise<void> {
  if (discoveredReadersListener) {
    await discoveredReadersListener.remove();
    discoveredReadersListener = null;
  }
}

export async function discoverReaders(businessId: string): Promise<ReaderInterface[]> {
  const generation = ++activeDiscoveryGeneration;
  let latestReaders: ReaderInterface[] = [];
  let discoverySettled = false;

  const isStale = () => generation !== activeDiscoveryGeneration;

  const finish = async (readers: ReaderInterface[], error?: string) => {
    if (discoverySettled || isStale()) return readers;
    discoverySettled = true;
    await cancelDiscovery();
    await removeDiscoveredReadersListener();
    if (error) {
      emitStatus('error', error);
      throw new Error(error);
    }
    emitStatus('idle');
    return readers;
  };

  emitStatus('discovering');
  console.log('[readerService] discoverReaders START', { businessId, generation });

  const runDiscovery = async (): Promise<ReaderInterface[]> => {
    console.log('[readerService] step 1: ensureInitialized');
    await withTimeout(
      ensureInitialized(businessId),
      TOKEN_FETCH_TIMEOUT_MS,
      'Terminal failed to start (plugin is unresponsive). Force-quit the app and try again.'
    );
    const terminal = getTerminal();
    console.log('[readerService] step 1 done: terminal ready');

    console.log('[readerService] step 2: fetch connection token');
    const { locationId } = await withTimeout(
      fetchConnectionToken(businessId),
      TOKEN_FETCH_TIMEOUT_MS,
      'Could not reach the payment server. Check your internet connection and try again.'
    );
    console.log('[readerService] step 2 done: got locationId', locationId);

    await cancelDiscovery();
    await removeDiscoveredReadersListener();

    discoveredReadersListener = await terminal.addListener(
      TerminalEventsEnum.DiscoveredReaders,
      ({ readers }) => {
        const list = readers || [];
        if (list.length > 0) {
          latestReaders = list;
          if (discoveredReadersCallback) discoveredReadersCallback(list);
        }
      }
    );

    // Listen for native failures so we are not stuck if the plugin never resolves.
    const failedListener = await terminal.addListener(TerminalEventsEnum.Failed, (info: { message?: string }) => {
      if (!isStale()) {
        emitStatus('error', info?.message || 'Reader discovery failed');
      }
    });

    console.log('[readerService] step 3: native discoverReaders call');
    try {
      const { readers } = await terminal.discoverReaders({
        type: TerminalConnectTypes.Bluetooth,
        locationId,
        bluetoothScanWaitTime: BLUETOOTH_SCAN_MS,
      });
      console.log('[readerService] step 3 done: readers returned', readers?.length || 0);
      return readers?.length ? readers : latestReaders;
    } finally {
      await failedListener.remove().catch(() => {});
    }
  };

  const watchdog = new Promise<ReaderInterface[]>((resolve, reject) => {
    setTimeout(async () => {
      if (discoverySettled || isStale()) return;
      try {
        const readers = await finish(
          latestReaders,
          latestReaders.length > 0
            ? undefined
            : 'Reader search timed out after 25 seconds. Enable Bluetooth for this app under Settings → Privacy & Security → Bluetooth, power on the WisePad 3, and confirm it is registered in Stripe Terminal → Readers.'
        );
        resolve(readers);
      } catch (e) {
        reject(e);
      }
    }, DISCOVERY_OVERALL_TIMEOUT_MS);
  });

  try {
    const result = await Promise.race([runDiscovery(), watchdog]);
    return await finish(result);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Discovery failed';
    return await finish(latestReaders, message);
  }
}

export type AutoConnectResult =
  | { outcome: 'connected'; reader: ReaderInterface | null }
  | { outcome: 'needs-selection'; readers: ReaderInterface[] }
  | { outcome: 'none' };

/**
 * One-tap reader connection for checkout.
 *
 * Starts Bluetooth discovery and decides what to do the moment readers appear (via the
 * early DiscoveredReaders event — we do NOT wait for the full scan window to elapse):
 *   • If the business's previously-connected reader is found → connect to it instantly.
 *   • If exactly one reader is found (and none is remembered) → connect to it.
 *   • If several unknown readers are found → return them so the UI shows a picker.
 *   • If nothing is found before the timeout → return 'none'.
 *
 * This collapses the old "search → pick → connect" sequence into a single automatic step
 * for the common case (same reader, every day), while still handling first-time / multi-reader.
 */
export async function autoConnectReader(businessId: string): Promise<AutoConnectResult> {
  if (connectedReader) return { outcome: 'connected', reader: connectedReader };

  const generation = ++activeDiscoveryGeneration;
  const isStale = () => generation !== activeDiscoveryGeneration;
  const rememberedSerial = getRememberedReaderSerial(businessId);

  emitStatus('discovering');
  console.log('[readerService] autoConnectReader START', { businessId, generation, rememberedSerial });

  await withTimeout(
    ensureInitialized(businessId),
    TOKEN_FETCH_TIMEOUT_MS,
    'Terminal failed to start (plugin is unresponsive). Force-quit the app and try again.'
  );
  if (isStale()) return { outcome: 'none' };

  const terminal = getTerminal();

  // locationId is needed to scope discovery. Reuse the cached one when we have it so we skip an
  // extra edge-function round trip; otherwise fetch once. (The SDK fetches its own fresh token via
  // the RequestedConnectionToken listener at connect time, which is also when tipping config syncs.)
  let locationId = cachedLocationId;
  if (!locationId) {
    const tok = await withTimeout(
      fetchConnectionToken(businessId),
      TOKEN_FETCH_TIMEOUT_MS,
      'Could not reach the payment server. Check your internet connection and try again.'
    );
    locationId = tok.locationId;
  }
  if (isStale()) return { outcome: 'none' };

  await cancelDiscovery();
  await removeDiscoveredReadersListener();

  let latestReaders: ReaderInterface[] = [];

  type Decision =
    | { kind: 'connect'; reader: ReaderInterface }
    | { kind: 'select'; readers: ReaderInterface[] }
    | { kind: 'none' };

  const decision = await new Promise<Decision>((resolve) => {
    let settled = false;
    const settle = (d: Decision) => {
      if (settled || isStale()) return;
      settled = true;
      resolve(d);
    };

    // Decide based on the readers we've seen so far. `isFinal` is true once the native scan has
    // fully resolved (or the watchdog fired) — at that point we stop holding out for a remembered
    // reader and act on whatever we have.
    const evaluate = (readers: ReaderInterface[], isFinal: boolean) => {
      latestReaders = readers && readers.length ? readers : latestReaders;
      const list = latestReaders;
      if (list.length > 0 && discoveredReadersCallback) discoveredReadersCallback(list);

      if (list.length === 0) {
        if (isFinal) settle({ kind: 'none' });
        return;
      }

      if (rememberedSerial) {
        const match = list.find((r) => r.serialNumber === rememberedSerial);
        if (match) {
          settle({ kind: 'connect', reader: match });
          return;
        }
        // We have readers but not the remembered one yet. Keep scanning in case it appears,
        // unless this is the final result — then fall through to single/select handling.
        if (!isFinal) return;
      }

      if (list.length === 1) {
        settle({ kind: 'connect', reader: list[0] });
        return;
      }

      settle({ kind: 'select', readers: list });
    };

    // Early, incremental discovery results.
    terminal
      .addListener(TerminalEventsEnum.DiscoveredReaders, ({ readers }) => {
        evaluate(readers || [], false);
      })
      .then((listener) => {
        discoveredReadersListener = listener;
      })
      .catch(() => {});

    // Kick off the native scan. Its resolution is treated as the final result.
    terminal
      .discoverReaders({
        type: TerminalConnectTypes.Bluetooth,
        locationId: locationId!,
        bluetoothScanWaitTime: BLUETOOTH_SCAN_MS,
      })
      .then(({ readers }) => evaluate(readers || latestReaders, true))
      .catch(() => evaluate(latestReaders, true));

    // Overall safety timeout.
    setTimeout(() => evaluate(latestReaders, true), DISCOVERY_OVERALL_TIMEOUT_MS);
  });

  await cancelDiscovery();
  await removeDiscoveredReadersListener();
  if (isStale()) return { outcome: 'none' };

  if (decision.kind === 'connect') {
    await connectReader(businessId, decision.reader);
    return { outcome: 'connected', reader: connectedReader };
  }

  if (decision.kind === 'select') {
    emitStatus('idle');
    return { outcome: 'needs-selection', readers: decision.readers };
  }

  emitStatus('idle');
  return { outcome: 'none' };
}

/**
 * Connect to a specific Bluetooth reader.
 */
export async function connectReader(
  businessId: string,
  reader: ReaderInterface
): Promise<void> {
  emitStatus('connecting');

  await ensureInitialized(businessId);
  const terminal = getTerminal();

  // Set up event listeners for connected reader
  if (disconnectListener) await disconnectListener.remove();
  if (displayMessageListener) await displayMessageListener.remove();
  if (inputRequestListener) await inputRequestListener.remove();
  if (batteryListener) await batteryListener.remove();
  if (updateAvailableListener) await updateAvailableListener.remove();
  if (updateStartListener) await updateStartListener.remove();
  if (updateProgressListener) await updateProgressListener.remove();
  if (updateFinishListener) await updateFinishListener.remove();

  disconnectListener = await terminal.addListener(
    TerminalEventsEnum.DisconnectedReader,
    ({ reason }) => {
      connectedReader = null;
      notifyReaderChange();
      emitStatus('idle', reason ? `Disconnected: ${reason}` : undefined);
    }
  );

  displayMessageListener = await terminal.addListener(
    TerminalEventsEnum.RequestDisplayMessage,
    ({ message }) => {
      emitDisplay(message);
    }
  );

  inputRequestListener = await terminal.addListener(
    TerminalEventsEnum.RequestReaderInput,
    ({ message }) => {
      emitStatus('waiting_for_card');
      emitDisplay(message);
    }
  );

  batteryListener = await terminal.addListener(
    TerminalEventsEnum.BatteryLevel,
    ({ level, status }) => {
      if (connectedReader) {
        connectedReader = { ...connectedReader, batteryLevel: level, batteryStatus: status };
      }
    }
  );

  // Reader firmware update events — these can take 5-15 minutes on first connect.
  updateAvailableListener = await terminal.addListener(
    TerminalEventsEnum.ReportAvailableUpdate,
    () => {
      console.log('[readerService] firmware update available');
      emitDisplay('Reader update available...');
    }
  );

  updateStartListener = await terminal.addListener(
    TerminalEventsEnum.StartInstallingUpdate,
    () => {
      console.log('[readerService] installing firmware update');
      firmwareUpdating = true;
      firmwareUpdateProgress = 0;
      emitDisplay('Updating reader firmware... Keep the reader nearby and powered on.');
    }
  );

  updateProgressListener = await terminal.addListener(
    TerminalEventsEnum.ReaderSoftwareUpdateProgress,
    ({ progress }) => {
      const pct = Math.round((progress ?? 0) * 100);
      firmwareUpdateProgress = pct;
      console.log('[readerService] firmware update progress', pct);
      emitDisplay(`Updating reader firmware… ${pct}%`);
    }
  );

  updateFinishListener = await terminal.addListener(
    TerminalEventsEnum.FinishInstallingUpdate,
    () => {
      console.log('[readerService] firmware update finished');
      firmwareUpdating = false;
      firmwareUpdateProgress = 100;
      emitDisplay('Reader update complete.');
    }
  );

  try {
    console.log('[readerService] connecting to reader', reader.serialNumber);
    emitDisplay('Pairing with reader… If this is the first time, a firmware update may take several minutes.');
    await terminal.connectReader({
      reader,
      autoReconnectOnUnexpectedDisconnect: true,
    });
    await syncConnectedReaderFromSdk();
    if (!connectedReader) connectedReader = reader;
    notifyReaderChange();
    rememberReader(businessId, connectedReader?.serialNumber || reader.serialNumber);
    console.log('[readerService] reader connected', connectedReader?.serialNumber);
    emitStatus('connected');
  } catch (err: any) {
    connectedReader = null;
    notifyReaderChange();
    emitStatus('error', err?.message || 'Connection failed');
    throw err;
  }
}

/**
 * Disconnect from the current reader.
 */
export async function disconnectCurrentReader(): Promise<void> {
  if (!connectedReader) return;

  try {
    const terminal = getTerminal();
    await terminal.disconnectReader();
  } catch {
    // Already disconnected
  }
  connectedReader = null;
  notifyReaderChange();
  emitStatus('idle');
}

/**
 * Process a payment on the connected Bluetooth reader.
 * Full flow: create PI → collect payment method → confirm.
 */
export async function processReaderPayment(
  params: ReaderPaymentParams
): Promise<{ paymentIntentId: string }> {
  if (!connectedReader) {
    throw new Error('No reader connected. Please connect a reader first.');
  }

  const terminal = getTerminal();
  const { businessId, amount, paymentType, bookingId, proposalId, description } = params;

  emitStatus('processing');
  emitDisplay('Creating payment...');

  // 1) Create Terminal PaymentIntent (card_present)
  const { data: piData, error: piError } = await supabase.functions.invoke(
    'create-terminal-payment-intent',
    {
      body: {
        businessId,
        amount,
        payment_type: paymentType,
        bookingId: bookingId || undefined,
        proposalId: proposalId || undefined,
        description: description || 'Card reader payment',
        source: 'card_reader',
      },
    }
  );

  if (piError || !piData?.paymentIntentId || !piData?.clientSecret) {
    emitStatus('error', piData?.error || 'Failed to create payment');
    throw new Error(piData?.error || piError?.message || 'Failed to create payment');
  }

  const paymentIntentId = piData.paymentIntentId as string;
  const clientSecret = piData.clientSecret as string;
  // Server tells us whether the business has tipping enabled (drives reader prompt via Location config).
  const tipsEnabled = !!piData.tipsEnabled;

  // 2) Collect payment method (reader prompts customer)
  emitStatus('waiting_for_card');
  emitDisplay(tipsEnabled ? 'Present card — tip prompt will appear on reader' : 'Present card on reader...');

  try {
    // For BBPOS WisePad 3, tipping is driven by the Location's Terminal Configuration (set server-side
    // in `terminal-connection-token` / `create-terminal-payment-intent`). Passing a TippingConfiguration
    // here causes "tipping configuration provided with incompatible reader" because the SDK only allows
    // this on Tap to Pay on iPhone. So we DO NOT pass tipEligibleAmount on the Bluetooth path.
    await terminal.collectPaymentMethod({ paymentIntent: clientSecret });
  } catch (err: any) {
    emitStatus('error', err?.message || 'Card collection failed');
    throw err;
  }

  // 3) Confirm payment
  emitStatus('confirming');
  emitDisplay('Processing payment...');

  try {
    await terminal.confirmPaymentIntent();
  } catch (err: any) {
    emitStatus('error', err?.message || 'Payment confirmation failed');
    throw err;
  }

  emitStatus('success');
  emitDisplay('Payment successful!');

  return { paymentIntentId };
}

/**
 * Cancel an in-progress payment collection.
 */
export async function cancelPayment(): Promise<void> {
  try {
    const terminal = getTerminal();
    await terminal.cancelCollectPaymentMethod();
  } catch {
    // May already be cancelled
  }
  emitStatus('connected');
}

/**
 * Check if the Stripe Terminal Bluetooth feature is available on this device.
 */
export async function isBluetoothReaderAvailable(): Promise<boolean> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return false;
    const terminal = getTerminal();
    return typeof terminal?.initialize === 'function';
  } catch {
    return false;
  }
}

/**
 * Clean up all listeners and state.
 */
export async function cleanup(): Promise<void> {
  await cancelDiscovery();
  await disconnectCurrentReader();
  if (tokenListener) { await tokenListener.remove(); tokenListener = null; }
  if (disconnectListener) { await disconnectListener.remove(); disconnectListener = null; }
  if (displayMessageListener) { await displayMessageListener.remove(); displayMessageListener = null; }
  if (inputRequestListener) { await inputRequestListener.remove(); inputRequestListener = null; }
  if (batteryListener) { await batteryListener.remove(); batteryListener = null; }
  if (updateAvailableListener) { await updateAvailableListener.remove(); updateAvailableListener = null; }
  if (updateStartListener) { await updateStartListener.remove(); updateStartListener = null; }
  if (updateProgressListener) { await updateProgressListener.remove(); updateProgressListener = null; }
  if (updateFinishListener) { await updateFinishListener.remove(); updateFinishListener = null; }
  connectedReader = null;
  notifyReaderChange();
  initialized = false;
  currentBusinessId = null;
  cachedLocationId = null;
  lastStatus = 'idle';
  lastStatusMessage = null;
  lastDisplayMessage = '';
  firmwareUpdateProgress = null;
  firmwareUpdating = false;
  removeCallbacks();
}
